import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";

import CommentComp from "../../components/Comment/Comment";
import { IBuyCarMenuPolymer, IPriceVote } from "@Types/buyCarMenu";
import { ECommentType, EHistoryRecordType } from "@Types/constants";
import { fetchBuyCarMenuPolymer, fetchMenuUserStatus, getMenuInfo } from "../../services/buyCarMenu";

import withLayout from "../../layouts/withLayout";
import { useAtom } from "jotai";
import { loginAtom } from "@Lib/jotai/login";
import { Breadcrumb, Col, Row, Skeleton } from "antd";
import { host } from "@Services/url";
import { EyesIcon, LikeFillIcon, LikeIcon, UsersIcon } from "@t8891/icon";
import ScrollImage from "@Components/buyCarMenu/Menu/scrollImage";
import { fetchLike } from "@Services/buyCar";
import { FbAndLine } from "@Components/buyCarMenu/ShareItem";
import {
  AdRight,
  AwaitBrandServer,
  MenuEntry,
  RankingList,
  SeekMenu,
  ShareMenu,
} from "@Components/buyCarMenu/right/common";
import { formatThousend } from "../../utils/helper";
import SplitText from "@Components/SplitText";
import { useInViewport, useMount } from "ahooks";
import { setUrlFormat } from "@Lib/common";
import AdIframeImage from "@Components/ad/AdFrame_v2";
import MenuDetialsList from "@Components/Comment/MenuDetialsList";

const breadcrumbCls = "text-[#333] text-14 font-normal";

const BottomFixedBanner = React.memo(({ isShare, id, onClick }: any) => {
  const bottomRef = useRef<any>(null);
  const [inViewport] = useInViewport(bottomRef);
  return (
    <>
      <div ref={bottomRef}></div>
      <div
        style={{ position: inViewport ? "relative" : "fixed" }}
        className='left-0 bottom-0 right-0 z-99 w-screen flex justify-center h-80 leading-80 items-center bg-[#F0F6FF]'
      >
        <div className='w-[1190px] h-full flex items-center'>
          <div className='w-[930px] flex justify-between items-center'>
            <span className='text-20 text-[#333]'>
              計劃購車？立即發佈徵求菜單，最快<span className='text-[#F87F15] text-32'>2</span>分鐘報價
            </span>

            <a
              href={`${host.c}/buy-car/menu/seek/form?id=${id}&source=${
                isShare ? "菜單分享詳情頁-底部徵求菜單" : "徵求菜單詳情頁-底部徵求菜單"
              }`}
              target='_blank'
              className='relative px-20 h-50 flex items-center rounded-6 bg-[#3264ff] text-[#fff] text-20 cursor-pointer hover:text-[#fff]'
              onClick={onClick}
            >
              發佈徵求菜單
              <span className='bg-[#FFCC32] w-[106px] h-18 leading-18 rounded-4 text-center text-12 text-[#664004] absolute right-0 -top-16'>
                最快2分鐘報價
              </span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
});

const MenuDetails: React.FC = () => {
  const id = window?.MENU_ID as string;
  const [loginInfo, updateLoginInfo] = useAtom(loginAtom);

  const initData: IBuyCarMenuPolymer = {
    hotMenuKindRank: [],
    inquiryCount: 0,
    latest3MonthAgents: 0,
    seekMenuCount: 0,
    shareMenuCount: 0,
    unfinishedUsers: 0,
    waitingServiceKindRank: [],
  };
  /** 獲取詳情 */
  const { data: details } = useQuery(["getMenuInfo", id], async () => {
    const res = await getMenuInfo({ id });
    if (Number(res.status) === 12000 && res.data) {
      return res.data;
    }
    return null;
  });

  const { data = initData } = useQuery<IBuyCarMenuPolymer>([`fetchBuyCarMenuPolymer`], async () => {
    const res = await fetchBuyCarMenuPolymer();
    return res?.status === 12000 ? res.data : initData;
  });

  const { inquiryCount, shareMenuCount, seekMenuCount, hotMenuKindRank, waitingServiceKindRank, latest3MonthAgents } =
    data;

  const { data: userStatus = { isFavorite: 0, liked: 0, likes: 0, views: 0 }, refetch } = useQuery(
    ["likeStatus"],
    async () => {
      const res = await fetchMenuUserStatus({ id });
      if (res.status === 12000) return res.data as any;
      return { isFavorite: 0, liked: 0, likes: 0, views: 0 };
    },
  );

  const { mutate } = useMutation<unknown, unknown, any, unknown>(async (params) => {
    const nextAction = "like";
    const res = await fetchLike({
      id: id,
      model: "buycarMenu",
      action: nextAction,
    });
    if (res?.status === 12000) {
      refetch();
    }
  });

  const { brandName, kindName, modelName, user, agentBiddingLink } = details || {};
  const isShare = details?.details?.type === 1;
  const isSeek = details?.details?.type === 2;
  return (
    <section>
      <div className='w-screen bg-[#fff]'>
        <div className='w-[1190px] relative flex flex-col' style={{ margin: "0 auto" }}>
          <div className='h-[50px] flex items-center'>
            <Breadcrumb separator=''>
              <Breadcrumb.Item className={breadcrumbCls}>您當前的位置</Breadcrumb.Item>
              <Breadcrumb.Separator>:</Breadcrumb.Separator>
              <Breadcrumb.Item href='https://c.8891.com.tw/Models'>
                <span className={breadcrumbCls}>新車</span>
              </Breadcrumb.Item>
              <Breadcrumb.Separator> {">"} </Breadcrumb.Separator>
              <Breadcrumb.Item href={`${host.c}/buycar/menu`}>
                <span className={breadcrumbCls}> 購車菜單</span>
              </Breadcrumb.Item>
              <Breadcrumb.Separator> {">"} </Breadcrumb.Separator>
              <Breadcrumb.Item href={`${host.c}/menu/${setUrlFormat(brandName)}`} className={breadcrumbCls}>
                <span className={breadcrumbCls}>{brandName || "廠牌"} 菜單</span>
              </Breadcrumb.Item>
              <Breadcrumb.Separator> {">"} </Breadcrumb.Separator>
              <Breadcrumb.Item
                href={`${host.c}/menu/${setUrlFormat(brandName)}/${setUrlFormat(kindName)}`}
                className={breadcrumbCls}
              >
                <span className={breadcrumbCls}>{kindName || "車款"} 菜單</span>
              </Breadcrumb.Item>
              <Breadcrumb.Separator> {">"} </Breadcrumb.Separator>
              <Breadcrumb.Item className={breadcrumbCls}>
                <span className={breadcrumbCls}>{details?.details?.type === 2 ? "徵求菜單詳情" : "菜單分享詳情"}</span>
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <div className='flex w-full justify-between'>
            <div className='flex flex-col'>
              {!details?.details ? (
                <Skeleton className='mb-50' active paragraph={{ rows: 4, width: 930 }} />
              ) : (
                <div
                  className='w-[930px] border-1 border-solid border-[#3264ff] rounded-4 overflow-hidden relative'
                  style={{
                    background: "linear-gradient(0deg,#ffffff, #f9fcff 50%)",
                    boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.10)",
                  }}
                >
                  <div className='w-full h-60 flex justify-between items-center px-20'>
                    <h1 className='text-22 text-[#333] font-[400]'>
                      <span className='mr-12'>{details?.details?.type === 2 ? "徵求菜單" : "菜單分享"}</span>
                      <span>
                        {brandName} {kindName} {modelName}
                      </span>
                    </h1>
                    <div className='text-14 text-[#333] flex items-center'>
                      <EyesIcon size={40} fill='#333' className='mr-8' />
                      {!!userStatus?.views ? formatThousend(userStatus?.views) : 1}
                    </div>
                  </div>
                  <div className='w-full h-40 px-20 flex items-center text-[#999999] text-14 font-light'>
                    {!!details?.details?.isCheck && (
                      <img
                        className='w-[118px] h-[20px] mr-16'
                        src='https://photo.8891.com.tw/2024/08/05/1722843092820728.png'
                        alt='icon'
                      ></img>
                    )}
                    {details?.details && (
                      <span className='mr-16'>
                        {details?.details?.typeName}者 {user?.mobile}
                      </span>
                    )}

                    {details?.postDate && <span>發佈時間 {details?.postDate}</span>}
                  </div>

                  <div className='flex flex-col gap-14 p-20 pb-66 relative'>
                    {details?.details?.options?.map((item) => {
                      const isFullOfRow = ["配備", "購車心得", "保險"].includes(item.label);
                      return (
                        <div>
                          <div className='w-full flex'>
                            <div className='flex leading-[34px]'>
                              <div className='w-68 mr-14 text-[#999] text-16 flex items-start '>
                                <SplitText label={item.label} width={58}></SplitText>
                              </div>
                            </div>
                            <div
                              className={
                                item.color
                                  ? `text-[${item.color}] text-24 flex items-center flex-wrap gap-4`
                                  : "text-[#333] text-18 flex-1 flex items-center flex-wrap gap-4"
                              }
                            >
                              <div
                                className={`${!!item.remark ? "" : "w-800"} leading-[34px] break-words`}
                                dangerouslySetInnerHTML={{ __html: item.content }}
                              />
                              {!!item.remark && (
                                <span className='ml-4 text-14 leading-20 text-[#666]'>{item.remark}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <a
                      href={`/kind-summary/${details?.kindId}`}
                      target='_blank'
                      className='w-140 whitespace-pre h-34 flex items-center pl-20 bg-[length:100%_100%] bg-[url(https://photo.8891.com.tw/2025/01/08/1736329684446895.png)] absolute -bottom-2 right-[262px] text-16 text-[#5474FC] font-normal hover:text-[#5474FC] no-repeat'
                    >
                      查看車款資訊 {">"}
                    </a>
                    <a
                      className='text-16 absolute bg-contain justify-center pl-12 -bottom-2 -right-2 hover:text-[#fff] text-[#fff] text-center flex items-center w-[274px] h-[34px] bg-[url(https://photo.8891.com.tw/2025/01/09/1736404388705044.png)]'
                      target='_blank'
                      id='detail-seek-btn'
                      data-source={
                        details?.details?.type === 2 ? "徵求菜單詳情頁-我也去徵求" : "菜單分享詳情頁-我也去徵求"
                      }
                      href={`${host.c}/buy-car/menu/seek/form?id=${id}&source=${
                        details?.details?.type === 2 ? "徵求菜單詳情頁-我也去徵求" : "菜單分享詳情頁-我也去徵求"
                      }`}
                      onClick={() => {
                        if (!userStatus?.liked) {
                          mutate({});
                        }
                      }}
                    >
                      8891補貼最高10萬，我也去徵求 {">"}
                    </a>
                  </div>
                </div>
              )}
              <div className='flex justify-between items-center'>
                {details?.details && (
                  <div className='flex mt-16'>
                    {isShare ? (
                      <div
                        id='liked-btn'
                        className='h-50 rounded-8 text-18 gap-8 flex items-center cursor-pointer border-[#3264ff] border-1 border-solid px-10 text-[#333]'
                        onClick={() => {
                          if (!userStatus?.liked) {
                            mutate({});
                          }
                        }}
                      >
                        {userStatus?.liked ? (
                          <LikeFillIcon size={48} fill='#3264ff' />
                        ) : (
                          <LikeIcon size={48} fill='#333' />
                        )}
                        {userStatus?.likes! > 0 ? (
                          <>
                            <span className='text-[#FF4433]'>{userStatus?.likes || 0}</span>人按讚好菜單!
                          </>
                        ) : (
                          "好菜單"
                        )}
                        {userStatus.likes >= 3 && <ScrollImage />}
                      </div>
                    ) : (
                      <div className='text-18 text-[#333] flex items-center'>
                        {details?.details?.assignIntro.includes("位業代") && <UsersIcon size={30} fill='#333' />}

                        <span dangerouslySetInnerHTML={{ __html: details?.details?.assignIntro }} />

                        {agentBiddingLink && (
                          <a
                            className='h-32 ml-16 leading-32 text-center text-16 text-[#999]'
                            target='_blank'
                            id='agent-assign-entry'
                            href={agentBiddingLink}
                            style={{ textDecoration: "underline" }}
                          >
                            搶先獲客 {">"}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className='flex items-center text-18 text-[#333] gap-12 mt-16'>
                  分享至
                  <FbAndLine id={Number(id)} link={details?.shareUrl!} className='flex mr-4 gap-12' />
                </div>
              </div>
              <div className='mt-40'>
                <CommentComp
                  type={details?.details?.type}
                  id={id}
                  isSign={!!loginInfo || false}
                  emptyConetent={<></>}
                />
              </div>
              {!!details?.kindId && (
                <MenuDetialsList
                  kindId={details?.kindId}
                  menuId={id}
                  kindName={kindName}
                  brandName={brandName}
                  type={details?.details?.type}
                />
              )}
            </div>

            <div className='w-[240px] flex flex-col'>
              {isShare && (
                <MenuEntry
                  inquiryCount={inquiryCount}
                  shareMenuCount={shareMenuCount}
                  id={id}
                  source={isShare ? "菜單分享詳情頁-右側徵求菜單" : "徵求菜單詳情頁-右側徵求菜單"}
                />
              )}

              {/* 分享模塊 */}
              {/* {isShare && <ShareMenu inquiryCount={inquiryCount} shareMenuCount={shareMenuCount} id={id} />} */}

              {/* 待服務的前十廠牌 */}
              {isSeek && (
                <AwaitBrandServer
                  waitingServiceKindRank={waitingServiceKindRank}
                  latest3MonthAgents={latest3MonthAgents}
                />
              )}
              {/* 右側橫幅廣告 */}
              {/* <AdRight slot='/173427088/newcar-pc-menuDetails-right' /> */}
              <div className='relative'>
                <AdIframeImage
                  slot={"/173427088/newcar-pc-menuDetails-right"}
                  size={[["fluid"], [240, 200]]}
                  width={240}
                  height={200}
                  imgStyles={{
                    borderRadius: "4px",
                  }}
                  styles={{
                    marginBottom: "20px",
                  }}
                />
              </div>
              {/* 優質菜單排行 */}
              {isShare && <RankingList hotMenuKindRank={hotMenuKindRank} id={id} />}

              {isSeek && (
                <SeekMenu
                  seekMenuCount={seekMenuCount}
                  id={id}
                  source={isShare ? "菜單分享詳情頁-右側徵求菜單" : "徵求菜單詳情頁-右側徵求菜單"}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <BottomFixedBanner
        isShare={isShare}
        id={id}
        onClick={() => {
          if (!userStatus?.liked) {
            mutate({});
          }
        }}
      />
    </section>
  );
};

withLayout({
  cmp: <MenuDetails />,
  params: {
    AdTopBanner: false,
    nav: "DefaultFixedNav",
    footer: "Footer",
    needRTBH: true,
  },
});
